import { useState } from "react";
import type { JSX } from "react";
import { Button, Input } from "@fluentui/react-components";
import { AddRegular, ArrowRightRegular, CheckmarkRegular, CopyRegular, DismissRegular, PlayRegular, SparkleRegular } from "@fluentui/react-icons";
import { totalFragmentCount } from "@minglun/domain";
import type { OptimizationStudy } from "@minglun/domain";
import { activeStudy } from "../studies";
import { formatNumber, formatCompactNumber, useDialogFocus, type PageProps, solverResultStatus } from "../workbench-shared";

export function StudyWizard({ studies, initialSourceId, onCancel, onConfirm }: {
  studies: OptimizationStudy[];
  initialSourceId: string | undefined;
  onCancel(): void;
  onConfirm(values: { name: string; description: string; sourceId?: string }): void;
}): JSX.Element {
  const [sourceId, setSourceId] = useState<string | undefined>(initialSourceId);
  const source = studies.find((study) => study.id === sourceId);
  const [name, setName] = useState(() => initialSourceId ? `${studies.find((study) => study.id === initialSourceId)?.name ?? "方案"} 副本` : `优化方案 ${studies.length + 1}`);
  const [description, setDescription] = useState("");
  const existing = new Set(studies.map((study) => study.name.trim().toLocaleLowerCase("zh-CN")));
  const nameValid = Boolean(name.trim()) && !existing.has(name.trim().toLocaleLowerCase("zh-CN"));
  const dialogRef = useDialogFocus(true, onCancel);
  return <div className="modal-backdrop" role="presentation">
    <section ref={dialogRef} className="study-identity-dialog create-study-dialog" role="dialog" aria-modal="true" aria-labelledby="study-wizard-title" tabIndex={-1}>
      <header><h2 id="study-wizard-title">{initialSourceId ? '复制此方案' : '新建方案'}</h2><button type="button" onClick={onCancel} aria-label="关闭"><DismissRegular /></button></header>
      <form onSubmit={event=>{event.preventDefault();if(nameValid)onConfirm({name:name.trim(),description,...(sourceId?{sourceId}:{})});}}>
        <div className="wizard-form">
          <label><span>方案名称</span><Input data-dialog-autofocus value={name} onChange={(_,data)=>setName(data.value)} maxLength={40} /></label>
          <label><span>来源</span><select value={sourceId ?? ''} onChange={event=>setSourceId(event.target.value || undefined)}><option value="">空白方案</option>{studies.map(study=><option key={study.id} value={study.id}>{study.name}</option>)}</select><small>{source ? '带入资源与配置，不复制计算结果。' : '资源从零开始，使用默认参考属性和优化偏好。'}</small></label>
          <label><span>说明 <small>可选</small></span><textarea value={description} onChange={event=>setDescription(event.target.value)} maxLength={160} /></label>
          {!nameValid && <p className="field-error">请输入未被使用的方案名称。</p>}
        </div>
        <footer><Button appearance="subtle" onClick={onCancel}>取消</Button><Button type="submit" appearance="primary" disabled={!nameValid}>创建方案</Button></footer>
      </form>
    </section>
  </div>;
}

export function StudyIdentityDialog({ study, existingNames, onCancel, onConfirm }: { study: OptimizationStudy; existingNames: string[]; onCancel(): void; onConfirm(name: string, description: string): void }): JSX.Element {
  const [name, setName] = useState(study.name);
  const [description, setDescription] = useState(study.description ?? "");
  const normalized = name.trim().toLocaleLowerCase("zh-CN");
  const valid = Boolean(normalized) && !existingNames.some((item) => item.trim().toLocaleLowerCase("zh-CN") === normalized);
  const dialogRef = useDialogFocus(true, onCancel);
  return <div className="modal-backdrop" role="presentation"><section ref={dialogRef} className="study-identity-dialog" role="dialog" aria-modal="true" aria-labelledby="study-identity-title" tabIndex={-1}>
    <header><div><small>优化方案</small><h2 id="study-identity-title">编辑方案信息</h2></div><button type="button" onClick={onCancel} aria-label="关闭"><DismissRegular /></button></header>
    <div className="wizard-form"><label><span>方案名称</span><Input data-dialog-autofocus value={name} onChange={(_event, data) => setName(data.value)} maxLength={40} /></label><label><span>说明 <small>可选</small></span><textarea value={description} onChange={(event) => setDescription(event.target.value)} maxLength={160} /></label>{!valid && <p className="field-error">请输入未被使用的方案名称。</p>}</div>
    <footer><Button appearance="subtle" onClick={onCancel}>取消</Button><Button appearance="primary" disabled={!valid} onClick={() => onConfirm(name.trim(), description)}>确认</Button></footer>
  </section></div>;
}

export function StudyOverview({ workbench, userPlan, solve, solverProgress, solverAllowed, navigate }: PageProps): JSX.Element {
  const study = activeStudy(userPlan);
  const metricById = new Map(workbench.optimizationMetrics.map((metric) => [metric.id, metric]));
  const activeObjectives = userPlan.objectives
    .filter((item) => item.weight > 0)
    .sort((left, right) => right.weight - left.weight || (metricById.get(left.metricId)?.order ?? 0) - (metricById.get(right.metricId)?.order ?? 0));
  const requiredMetrics = workbench.optimizationMetrics
    .filter((metric) => metric.requiresBaseValue && metric.id !== "metric.global.element-resilience" && metric.id !== "metric.fate-value")
    .sort((left, right) => left.order - right.order);
  const baseById = new Map(userPlan.baseAttributes.map((item) => [item.metricId, item.value]));
  const enabledCharacters = workbench.characters.filter((character) => character.isEnabled);
  const inventoryById = new Map(userPlan.inventory.map((item) => [item.characterId, item]));
  const enteredCount = enabledCharacters.filter((character) => {
    const item = inventoryById.get(character.id);
    return Boolean(item && (item.wheelCount > 0 || totalFragmentCount(item) > 0));
  }).length;
  const optionNames = [
    userPlan.solver.includeDestinyWheel ? "宿命之轮" : undefined,
    userPlan.solver.ssrChoiceBalanceEnabled && userPlan.solver.ssrWheelChoices > 0 ? "SSR自选均衡" : undefined,
    userPlan.solver.fragmentAnalysisEnabled ? "碎片分析" : undefined,
  ].filter((item): item is string => Boolean(item));
  const studyCount = userPlan.studies?.length ?? 0;
  const linkedHistory = userPlan.solverHistory.find(
    (entry) => entry.studyId === study?.id || (!entry.studyId && studyCount <= 1),
  );
  const candidateResult = linkedHistory?.result ?? (studyCount <= 1 ? userPlan.lastResult : undefined);
  const result = candidateResult?.status === 'not_run' ? undefined : candidateResult;
  const objectiveSummary = activeObjectives.slice(0, 3).map((item) => `${metricById.get(item.metricId)?.name ?? item.metricId} ${item.weight}`).join(" · ");
  const referenceSummary = requiredMetrics.slice(0, 4).map((metric) => `${metric.name} ${formatCompactNumber(baseById.get(metric.id) ?? 0)}`).join(" · ");
  const ignoredCount = userPlan.inventory.filter((item) => item.ignoreFragments).length;
  return <main className="study-overview">
    <header className="study-overview-header">
      <div className="study-heading-copy">
        <h1>{study?.name ?? userPlan.name}</h1>
        {study?.description && <p>{study.description}</p>}
      </div>
      <div className="study-heading-actions">
        <Button appearance="secondary" disabled={!result} onClick={() => navigate("results")}>查看结果</Button>
      </div>
    </header>

    <section className="study-manifest" aria-label="方案配置">
      <StudySummaryRow
        index="01"
        title="目标权重"
        state={activeObjectives.length ? `${activeObjectives.length} 项` : "未配置"}
        summary={objectiveSummary || "至少设置一个大于 0 的目标权重"}
        warning={!activeObjectives.length}
        onClick={() => navigate("objectives")}
      />
      <StudySummaryRow
        index="02"
        title="参考属性"
        state={`${requiredMetrics.length} 项`}
        summary={referenceSummary || "尚未设置参考基础属性"}
        warning={!requiredMetrics.every((metric) => (baseById.get(metric.id) ?? 0) > 0)}
        onClick={() => navigate("base")}
      />
      <StudySummaryRow
        index="03"
        title="资源输入"
        state={`${enteredCount}/${enabledCharacters.length}`}
        summary={`SSR 自选 ${userPlan.solver.ssrWheelChoices} · 忽略碎片 ${ignoredCount ? `${ignoredCount} 个命轮` : "无"}`}
        onClick={() => navigate("resources")}
      />
      <StudySummaryRow
        index="04"
        title="计算配置"
        state="不限时"
        summary={`自动多核${optionNames.length ? ` · ${optionNames.join(" · ")}` : " · 基础求解"}`}
        onClick={() => navigate("solver")}
      />
    </section>

    <section className="study-latest" aria-labelledby="study-latest-title">
      <header><h2 id="study-latest-title">最近结果</h2><button type="button" onClick={() => navigate("results")} disabled={!result && !userPlan.solverHistory.length}>全部结果 <ArrowRightRegular /></button></header>
      {result ? <button type="button" className="latest-result-row" onClick={() => navigate("results") }>
        <span className={`status-pill ${result.status}`}>{solverResultStatus(result)}</span>
        <span><small>评分</small><strong>{result.objectiveScore === undefined ? "—" : formatNumber(result.objectiveScore, 2)}</strong></span>
        <span><small>耗时</small><strong>{formatNumber(result.elapsedMilliseconds / 1000, 2)} 秒</strong></span>
        <span><small>关系命轮</small><strong>{result.fateWheels.length}</strong></span>
        <ArrowRightRegular />
      </button> : <div className="study-no-result"><span>尚未运行此方案</span><small>配置完成后，点击右上角“运行方案”。</small></div>}
    </section>
  </main>;
}

export function StudySummaryRow({ index, title, state, summary, warning = false, onClick }: { index: string; title: string; state: string; summary: string; warning?: boolean; onClick(): void }): JSX.Element {
  return <button type="button" className={`study-summary-row ${warning ? "warning" : ""}`} onClick={onClick}>
    <span className="summary-index">{index}</span>
    <strong>{title}</strong>
    <span className="summary-state">{state}</span>
    <span className="summary-copy">{summary}</span>
    <span className="summary-action">编辑 <ArrowRightRegular /></span>
  </button>;
}

